<template>
  <div>
    <v-toolbar flat height="90">
      <ExportLinks export-key="company-customers" :filters="exportFilters" />

      <v-btn
        :disabled="!getPermission('customers.create')"
        color="secondary"
        class="text-capitalize ml-2"
        depressed
        v-text="translations.addNewCustomer[lang]"
        @click="openCustomerDataDialog('create', {})"
      ></v-btn>
      <v-spacer></v-spacer>
      <v-btn
        color="secondary"
        class="text-capitalize ml-2"
        depressed
        v-text="translations.importCustomers[lang]"
        @click="openImportDialog"
      ></v-btn>
      <v-spacer></v-spacer>
      <v-btn
        :disabled="!getPermission('customers.filters.create')"
        color="secondary"
        class="text-capitalize px-5 mr-3"
        depressed
        v-text="translations.visibleColumns[lang]"
        @click="columnsDialog = true"
      ></v-btn>
      <v-col cols="12" sm="4" class="pa-0">
        <b-search-field v-model="search"></b-search-field>
      </v-col>
    </v-toolbar>
    <CustomerFilters />
    <b-data-table
      :headers="visibleHeaders"
      :items="pageCustomers"
      :page.sync="page"
      @page-sync="(p) => (page = p)"
      :options.sync="options"
      @options-sync="(o) => (options = o)"
      :loading="loading"
    >
      <template v-slot:item.card_number="{ item }">
        <div v-if="item.cards && item.cards.length > 0">
          {{ item.cards.toString() }}
        </div>
        <div v-else>-</div>
      </template>
      <template v-slot:item.total="{ item }">
        {{ Number(item.current_balance) + Number(item.redeemed_points) }}
      </template>
      <template v-slot:item.points_expires_at="{ item }">
        {{ formatDate(item.points_expires_at) }}
      </template>
      <template v-slot:item.actions="{ item }">
        <v-menu offset-y left>
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip color="secondary" top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  color="secondary"
                  icon
                  v-bind="attrs"
                  v-on="{ ...tooltip, ...menu }"
                >
                  <v-icon v-text="icons.mdiDotsHorizontal"></v-icon>
                </v-btn>
              </template>
              <span class="font-weight-bold" v-text="translations.moreActions[lang]"></span>
            </v-tooltip>
          </template>
          <v-list dense>
            <v-list-item
              v-for="action in getCustomerActionItems(item)"
              :key="action.key"
              :disabled="action.disabled"
              @click="runCustomerAction(action)"
            >
              <v-list-item-icon class="mr-3">
                <v-icon color="secondary" v-text="action.icon"></v-icon>
              </v-list-item-icon>
              <v-list-item-title class="font-weight-bold">{{ action.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template
        v-for="attr in attributeDefs"
        v-slot:[`item.custom_attr_${attr.user_account_attribute_definition_id}`]="{ item }"
      >
        <div :key="attr.user_account_attribute_definition_id" class="d-flex align-center">
          <div
            class="d-flex flex-row align-center"
            v-if="
              !isEditing(
                item.user_account_id,
                `custom_attr_${attr.user_account_attribute_definition_id}`
              )
            "
            style="min-width: 140px; flex: 1 1 auto"
          >
            <div v-if="attr.attribute_type === 'multi_select'">
              <div
                v-if="
                  !item[`custom_attr_${attr.user_account_attribute_definition_id}`] ||
                  item[`custom_attr_${attr.user_account_attribute_definition_id}`] === '-'
                "
              >
                -
              </div>
              <div v-else class="d-flex flex-wrap">
                <v-chip
                  v-for="(chip, ci) in displayMultiValues(item, attr)"
                  :key="ci"
                  small
                  class="ma-1"
                >
                  {{ chip }}
                </v-chip>
              </div>
            </div>
            <div v-else>
              <span>{{
                item[`custom_attr_${attr.user_account_attribute_definition_id}`] || '-'
              }}</span>
            </div>
            <div style="min-width: 65px">
              <v-btn
                icon
                small
                :disabled="!getPermission('customers.update')"
                @click="startEditAttribute(item, attr)"
                class="ml-2"
              >
                <v-icon v-text="icons.mdiPencilOutline"></v-icon>
              </v-btn>
              <v-btn
                v-if="
                  item[`custom_attr_${attr.user_account_attribute_definition_id}`] &&
                  item[`custom_attr_${attr.user_account_attribute_definition_id}`] != '-'
                "
                icon
                small
                :disabled="!getPermission('customers.update')"
                class="ml-0"
                @click="confirmDeleteAttribute(item, attr)"
              >
                <v-icon v-text="icons.mdiDelete"></v-icon>
              </v-btn>
            </div>
          </div>
          <div v-else class="d-flex align-center" style="min-width: 200px; flex: 1 1 auto">
            <div v-if="attr.attribute_type === 'text' && attr.value_type === 'date'">
              <v-menu
                :close-on-content-click="false"
                offset-y
                max-width="290px"
                v-model="attributeEdits[item.user_account_id][`custom_attr_${attr.user_account_attribute_definition_id}`].editDateMenu"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    dense
                    outlined
                    hide-details
                    :value="
                      attributeEdits[item.user_account_id][
                        `custom_attr_${attr.user_account_attribute_definition_id}`
                      ].value
                    "
                    v-bind="attrs"
                    v-on="on"
                    clearable
                    @click:clear="
                      attributeEdits[item.user_account_id][
                        `custom_attr_${attr.user_account_attribute_definition_id}`
                      ].value = ''
                    "
                    style="max-width: 200px"
                  />
                </template>
                <v-date-picker
                  color="secondary"
                  no-title
                  v-model="
                    attributeEdits[item.user_account_id][
                      `custom_attr_${attr.user_account_attribute_definition_id}`
                    ].value
                  "
                  @input="attributeEdits[item.user_account_id][`custom_attr_${attr.user_account_attribute_definition_id}`].editDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </div>
            <div v-else-if="attr.attribute_type === 'text' && attr.value_type === 'datetime'">
              <v-menu
                :close-on-content-click="false"
                offset-y
                max-width="290px"
                v-model="attributeEdits[item.user_account_id][`custom_attr_${attr.user_account_attribute_definition_id}`].editDateMenu"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    dense
                    outlined
                    hide-details
                    :value="
                      attributeEdits[item.user_account_id][
                        `custom_attr_${attr.user_account_attribute_definition_id}`
                      ].value
                    "
                    v-bind="attrs"
                    v-on="on"
                    clearable
                    @click:clear="
                      attributeEdits[item.user_account_id][
                        `custom_attr_${attr.user_account_attribute_definition_id}`
                      ].value = ''
                    "
                    style="max-width: 200px"
                  />
                </template>
                <v-date-picker
                  color="secondary"
                  no-title
                  v-model="attributeEdits[item.user_account_id][`custom_attr_${attr.user_account_attribute_definition_id}`].editDateVal"
                  @input="attributeEdits[item.user_account_id][`custom_attr_${attr.user_account_attribute_definition_id}`].editTimeMenu = true"
                ></v-date-picker>
                <v-time-picker
                  v-if="attributeEdits[item.user_account_id][`custom_attr_${attr.user_account_attribute_definition_id}`].editTimeMenu"
                  color="secondary"
                  format="24hr"
                  use-seconds
                  v-model="attributeEdits[item.user_account_id][`custom_attr_${attr.user_account_attribute_definition_id}`].editTimeVal"
                  @click:second="
                    setDateTimeInlineEdit(item, attr);
                    attributeEdits[item.user_account_id][`custom_attr_${attr.user_account_attribute_definition_id}`].editDateMenu = false;
                    attributeEdits[item.user_account_id][`custom_attr_${attr.user_account_attribute_definition_id}`].editTimeMenu = false;
                  "
                ></v-time-picker>
              </v-menu>
            </div>
            <div v-else-if="attr.attribute_type === 'text'">
              <v-text-field
                dense
                outlined
                hide-details
                v-model="
                  attributeEdits[item.user_account_id][
                    `custom_attr_${attr.user_account_attribute_definition_id}`
                  ].value
                "
                style="max-width: 200px"
              />
            </div>

            <div v-else-if="attr.attribute_type === 'select'">
              <v-select
                dense
                outlined
                hide-details
                :items="
                  (attr.values || attr.options || []).map((v) => ({
                    text: v && v.label ? v.label : v,
                    val:
                      (v && (v.user_account_attribute_definition_value_id || v.value || v.id)) || v,
                  }))
                "
                item-text="text"
                item-value="val"
                v-model="
                  attributeEdits[item.user_account_id][
                    `custom_attr_${attr.user_account_attribute_definition_id}`
                  ].value
                "
                style="max-width: 200px"
              />
            </div>

            <div v-else-if="attr.attribute_type === 'radio'" style="max-width: 300px">
              <v-radio-group
                row
                dense
                v-model="
                  attributeEdits[item.user_account_id][
                    `custom_attr_${attr.user_account_attribute_definition_id}`
                  ].value
                "
              >
                <v-radio
                  v-for="(v, idx) in attr.values || attr.options || []"
                  :key="idx"
                  :label="v && v.label ? v.label : v"
                  :value="v && v.value !== undefined ? v.value : v && v.label ? v.label : v"
                  dense
                />
              </v-radio-group>
            </div>

            <div v-else-if="attr.attribute_type === 'multi_select'">
              <v-select
                dense
                outlined
                hide-details
                multiple
                :items="
                  (attr.values || attr.options || []).map((v) => ({
                    text: v && v.label ? v.label : v,
                    val:
                      (v && (v.user_account_attribute_definition_value_id || v.value || v.id)) || v,
                  }))
                "
                item-text="text"
                item-value="val"
                v-model="
                  attributeEdits[item.user_account_id][
                    `custom_attr_${attr.user_account_attribute_definition_id}`
                  ].value
                "
                style="max-width: 240px"
              />
            </div>

            <div v-else>
              <v-text-field
                dense
                outlined
                hide-details
                v-model="
                  attributeEdits[item.user_account_id][
                    `custom_attr_${attr.user_account_attribute_definition_id}`
                  ].value
                "
                style="max-width: 200px"
              />
            </div>

            <v-btn icon small @click="saveAttribute(item, attr)" class="ml-1">
              <v-icon v-text="icons.mdiCheck"></v-icon>
            </v-btn>
            <v-btn icon small @click="cancelEditAttribute(item, attr)" class="ml-1">
              <v-icon v-text="icons.mdiClose"></v-icon>
            </v-btn>
          </div>
        </div>
      </template>
    </b-data-table>
    <v-dialog
      v-model="customerDataDialog"
      :max-width="$vuetify.breakpoint.smAndDown ? '90%' : '40%'"
    >
      <CustomerForm
        v-if="customerDataDialog === true"
        :mode="mode"
        :dialog="customerDataDialog"
        @close="closeCustomerDataDialog"
        @openAddCardDialog="openAddCardDialog"
      />
    </v-dialog>
    <v-dialog v-model="deleteAttrDialog" max-width="500">
      <b-card
        type="delete"
        :title="translations.deletePermanently[lang]"
        @cancel="deleteAttrDialog = false"
        @submit="performDeleteAttribute"
        :submit-text="translations.submit"
        :cancel-text="translations.cancel"
        :loading="loading"
      >
        <v-row no-gutters>
          <v-col cols="12" md="12">
            <div>
              <p>
                {{
                  deleteAttrTarget && deleteAttrTarget.attr
                    ? `${translations.deleteAttributeConfirmation?.[lang] || 'Are you sure you want to delete'} '${deleteAttrTarget.attr.name}'?`
                    : translations.deletePermanently[lang]
                }}
              </p>
              <div v-if="deleteAttrValues && deleteAttrValues.length">
                <v-chip v-for="(v, i) in deleteAttrValues" :key="i" small class="ma-1">{{
                  v
                }}</v-chip>
              </div>
              <div v-else>-</div>
            </div>
          </v-col>
        </v-row>
      </b-card>
    </v-dialog>
    <v-dialog v-model="infoDialog" max-width="500">
      <AdditionalInfo @cancel="infoDialog = false" />
    </v-dialog>
    <v-dialog v-model="mergeDialog" max-width="500">
      <UserMergeDialog @cancel="mergeDialog = false" @merge="startMergeUsers" />
    </v-dialog>
    <v-dialog v-model="transDialog" max-width="1600" min-width="600px">
      <UserTransactionsDialog @cancel="transDialog = false" />
    </v-dialog>
    <v-dialog v-model="addCardDialog" max-width="500">
      <b-card
        :title="translations.addCard[lang]"
        @cancel="addCardDialog = false"
        @submit="saveCardNumber()"
        :submit-text="{
          el: 'Αποθήκευση',
          en: 'Save',
          it: 'Salva',
        }"
        :cancel-text="{
          el: 'Ακύρωση',
          en: 'Cancel',
          it: 'Cancella',
        }"
      >
        <v-row no-gutters>
          <v-col cols="12" md="12">
            <b-text-field
              :label="translations.card_number[lang]"
              v-model="newCardNumber"
            ></b-text-field>
          </v-col>
          <v-col cols="12" md="12">
            <b-text-field label="CVC" v-model="newCardCvc"></b-text-field>
          </v-col>
        </v-row>
      </b-card>
    </v-dialog>
    <v-dialog v-model="changeMobileDialog" max-width="500">
      <b-card
        :title="translations.changeMobile[lang]"
        @cancel="closeChangeMobileDialog()"
        @submit="saveNewMobile()"
        :submit-text="translations.save"
        :cancel-text="translations.cancel"
        :loading="loading"
      >
        <div class="d-flex align-center mobile-cc-container">
          <v-select
            :items="activeCountries"
            label="CC*"
            outlined
            v-model="newMobileCountryId"
            item-value="country_id"
            item-text="name"
            solo
            flat
            dense
            :hide-details="false"
            color="secondary"
            item-color="secondary"
            class="country-code-select mt-3"
          >
            <template v-slot:selection="{ item }">
              <v-row no-gutters class="d-flex align-center">
                <v-col class="mr-0">
                  <img :src="item.flag" width="25" style="vertical-align: middle" class="mr-0" />
                </v-col>
                <v-col cols="auto">{{ item.phone_code }}</v-col>
              </v-row>
            </template>
            <template v-slot:item="{ item }">
              <v-row no-gutters>
                <v-col class="mr-3">
                  <img :src="item.flag" width="25" style="vertical-align: middle" class="mr-3" />
                  {{ item.name }} ({{ item.code }})
                </v-col>
                <v-col cols="auto">{{ item.phone_code }}</v-col>
              </v-row>
            </template>
          </v-select>
          <v-text-field
            v-model="newMobile"
            :label="translations.mobile[lang]"
            color="secondary"
            class="mt-3 user-mobile"
            outlined
            dense
            clearable
            :hide-details="false"
          ></v-text-field>
        </div>
      </b-card>
    </v-dialog>
    <v-dialog v-model="clearPointsDialog" max-width="500">
      <b-card
        :title="translations[`clear_${storeMode}`][lang]"
        @cancel="closeClearPointsDialog()"
        @submit="removeAllPoints()"
        :submit-text="translations.submit"
        :cancel-text="translations.cancel"
        :loading="loading"
      >
        <v-row no-gutters>
          <v-col cols="12" md="12">
            <span>{{ translations[`clear_${storeMode}Confirmation1`][lang] }}</span>
            <v-col cols="12" md="12">
              <v-row v-if="clearPointsCustomer">
                <v-col cols="12" md="12"
                  ><span class="font-weight-bold">{{ translations.user_account_id[lang] }}</span
                  >: {{ clearPointsCustomer.user_account_id }}</v-col
                >
                <v-col cols="12" md="12"
                  ><span class="font-weight-bold">{{ translations.card_number[lang] }}</span
                  >: {{ clearPointsCustomer.card_number }}</v-col
                >
                <v-col cols="12" md="12"
                  ><span class="font-weight-bold">{{ translations.mobile[lang] }}</span
                  >: {{ clearPointsCustomer.mobile }}</v-col
                >
                <v-col cols="12" md="12"
                  ><span class="font-weight-bold">{{ translations.email[lang] }}</span
                  >: {{ clearPointsCustomer.email }}</v-col
                >
                <v-col cols="12" md="12"
                  ><span class="font-weight-bold">{{
                    translations[`current_balance_${storeMode}`][lang]
                  }}</span
                  >: {{ clearPointsCustomer.current_balance }}</v-col
                >
              </v-row>
            </v-col>
          </v-col>
        </v-row>
      </b-card>
    </v-dialog>
    <v-dialog v-model="detachCardDialog" max-width="500">
      <b-card
        :title="translations.detachCard[lang]"
        @cancel="closeDetachCardDialog()"
        @submit="detachCustomerCard()"
        :submit-text="translations.submit"
        :cancel-text="translations.cancel"
        :loading="loading"
      >
        <v-row no-gutters>
          <v-col cols="12" md="12">
            <span>{{ translations.detachCardConfirmation1[lang] }}</span>
            <v-col cols="12" md="12">
              <v-row v-if="detachCardCustomer">
                <v-col cols="12" md="12"
                  ><span class="font-weight-bold">{{ translations.user_account_id[lang] }}</span
                  >: {{ detachCardCustomer.user_account_id }}</v-col
                >
                <v-col v-if="detachCardCustomer.card_number" cols="12" md="12"
                  ><span class="font-weight-bold">{{ translations.card_number[lang] }}</span
                  >: {{ detachCardCustomer.card_number }}</v-col
                >
                <v-col v-else cols="12">
                  <b-select
                    :items="detachCardCustomer.cards"
                    :label="translations.card_number[lang]"
                    v-model="detachCardCustomer.card_number"
                  ></b-select>
                </v-col>
                <v-col cols="12" md="12"
                  ><span class="font-weight-bold">{{ translations.mobile[lang] }}</span
                  >: {{ detachCardCustomer.mobile }}</v-col
                >
                <v-col cols="12" md="12"
                  ><span class="font-weight-bold">{{ translations.email[lang] }}</span
                  >: {{ detachCardCustomer.email }}</v-col
                >
                <v-col cols="12" md="12"
                  ><span class="font-weight-bold">{{
                    translations[`current_balance_${storeMode}`][lang]
                  }}</span
                  >: {{ detachCardCustomer.current_balance }}</v-col
                >
              </v-row>
            </v-col>
          </v-col>
        </v-row>
      </b-card>
    </v-dialog>
    <v-dialog v-model="importDialog" max-width="500">
      <b-standard-card
        :title="translations.importCustomers[lang]"
        :cancel-text="translations.cancel"
        :submit-text="translations.submit"
        :loading="loading"
        class="mt-5"
        height="auto"
        :error-message="errorMessage"
        @submit="uploadCustomersFile()"
        @cancel="importDialog = false"
      >
        {{ translations.importCustomersDescription[lang] }}
        <v-row no-gutters align="center" class="mt-3">
          <v-col cols="5" class="pr-2"> </v-col>
          <v-col cols="11" class="pl-2">
            <v-file-input
              color="secondary"
              class="mt-1"
              outlined
              dense
              hide-details="auto"
              v-model="customersFile"
            ></v-file-input>
          </v-col>
        </v-row>
      </b-standard-card>
    </v-dialog>
    <v-dialog v-model="deleteCustomerDialog" max-width="500">
      <b-card
        type="delete"
        :title="translations.deleteCustomer[lang]"
        @cancel="deleteCustomerDialog = false"
        @submit="deletingCustomer()"
        :submit-text="translations.submit"
        :cancel-text="translations.cancel"
        :loading="loading"
      >
        <v-row no-gutters>
          <v-col cols="12" md="12">
            <span>{{ translations.deleteCustomerConfirmation1[lang] }}</span>
            <v-col v-if="deletingCustomerData" cols="12" md="12">
              <v-row v-if="deletingCustomerData.user_account_id">
                <v-col cols="12" md="12"
                  ><span class="font-weight-bold pr-2">ID:</span>
                  {{ deletingCustomerData.user_account_id }}</v-col
                >
                <v-col
                  v-if="
                    deletingCustomerData.first_name != '-' || deletingCustomerData.last_name != '-'
                  "
                  cols="12"
                  md="12"
                  ><span class="font-weight-bold pr-2">{{ translations.name[lang] }}:</span>
                  {{
                    `${
                      deletingCustomerData.first_name == '-' ? '' : deletingCustomerData.first_name
                    } ${
                      deletingCustomerData.last_name == '-' ? '' : deletingCustomerData.last_name
                    }`
                  }}</v-col
                >
                <v-col cols="12" md="12"
                  ><span class="font-weight-bold pr-2">{{ translations.mobile[lang] }}:</span>
                  {{ deletingCustomerData.mobile }}</v-col
                >
              </v-row>
            </v-col>
          </v-col>
        </v-row>
      </b-card>
    </v-dialog>
    <v-dialog v-model="columnsDialog" max-width="500">
      <b-card
        :title="translations.visibleColumns[lang]"
        :cancel-text="{ el: 'Κλείσιμο', en: 'close', it: 'Chiudi' }"
        @cancel="columnsDialog = false"
        :submitBtn="false"
      >
        <div class="subtitle-1 font-weight-medium pl-2">
          <v-checkbox
            v-for="(column, index) in visibleColumns"
            :key="index"
            color="secondary"
            class="mt-3 pt-0"
            hide-details
            v-model="column.isVisible"
          >
            <template v-slot:label>
              <h4 class="subtitle-2">{{ getVisibleColumnLabel(column) }}</h4>
            </template>
          </v-checkbox>
        </div>
      </b-card>
    </v-dialog>
    <v-dialog v-model="adjustPointsDialog" max-width="500">
      <b-card
        :title="translations.adjust_pointsDialogTitle[lang]"
        @cancel="closeAdjustPointsDialog"
        @submit="submitAdjustPoints"
        :loading="loading"
        :submit-text="translations.submit"
        :cancel-text="translations.cancel"
      >
        <v-row no-gutters>
          <v-col cols="12">
            <b-text-field
              v-model.number="adjustPoints.amount"
              type="number"
              :label="translations[`${storeMode}Amount`][lang]"
              :success="adjustPointsValidation.amount.success"
              :error-messages="adjustPointsValidation.amount.errorMessage"
              @blur="validatePointsAmount"
            />
          </v-col>

          <v-col cols="12" class="d-none">
            <v-autocomplete
              :items="loyaltiesData"
              v-model="adjustPoints.loyalty_program_id"
              :item-text="`name[${lang}]`"
              item-value="loyalty_program_id"
              menu-props="offsetY"
              :label="translations.loyalty[lang]"
              class="mt-4"
              outlined
              dense
              hide-details
              clearable
              :search-input.sync="searchLoyalties"
              :loading="loading"
              :disable-lookup="true"
              item-disabled="disable"
            >
              <template v-slot:selection="data">
                <v-chip
                  v-bind="data.attrs"
                  close
                  small
                  class="mt-1"
                  @click:close="removeSelectedLoyalty()"
                >
                  {{ data.item.name[lang] }}
                </v-chip>
              </template>
              <template v-slot:append-item>
                <div v-intersect="endIntersectLoyalties"></div>
                <v-list-item v-if="loading">
                  <v-list-item-action class="ml-1">
                    <v-row class="d-flex align-center">
                      <v-btn
                        class="text-capitalize"
                        depressed
                        fab
                        small
                        :loading="true"
                        style="background: transparent"
                      >
                      </v-btn>
                      <span class="pa-2">{{ translations.loadMore[lang] }}</span>
                    </v-row>
                  </v-list-item-action>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12">
            <b-textarea
              v-model="adjustPoints.reason"
              :label="translations.reason[lang]"
              :success="adjustPointsValidation.reason.success"
              :error-messages="adjustPointsValidation.reason.errorMessage"
              @blur="validatePointsReason"
            />
          </v-col>
        </v-row>
      </b-card>
    </v-dialog>
    <v-dialog v-model="redeemCouponsDialog" max-width="600">
      <CustomerCouponsRedeem :customer="redeemCouponsCustomer" @close="closeRedeemCouponsDialog" />
    </v-dialog>
    <v-dialog v-model="redemptionsDialog" max-width="1600" min-width="600px">
      <CustomerRedemptionsDialog
        v-if="redemptionsDialog && redemptionsUserAccountId"
        :userAccountId="redemptionsUserAccountId"
        @cancel="closeCustomerRedemptionsDialog"
      />
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import CustomerForm from '@/components/loyaltyPanel/customers/CustomerForm.vue';
import CustomerFilters from '@/components/loyaltyPanel/customers/CustomerFilters.vue';
import AdditionalInfo from '@/components/loyaltyPanel/customers/AdditionalInfo.vue';
import UserMergeDialog from '@/components/loyaltyPanel/customers/UserMergeDialog.vue';
import UserTransactionsDialog from '@/components/loyaltyPanel/customers/UserTransactionsDialog.vue';
import CustomerCouponsRedeem from '@/components/loyaltyPanel/customers/CustomerCouponsRedeem.vue';
import CustomerRedemptionsDialog from '@/components/loyaltyPanel/customers/CustomerRedemptionsDialog.vue';
import ExportLinks from '@/components/general/ExportLinks.vue';
import bSearchField from '@/components/base/BSearchField.vue';
import translations from '@/utils/translations/loyaltyPanel/customers';
import { cloneDeep, debounce } from 'lodash';
import moment from 'moment';

import {
  mdiPencilOutline,
  mdiMerge,
  mdiAccountCash,
  mdiCellphoneCog,
  mdiGiftOff,
  mdiCardBulletedOff,
  mdiDotsHorizontal,
  mdiDeleteAlert,
  mdiDelete,
  mdiPlusMinusVariant,
  mdiGiftOpen,
  mdiReceiptTextCheckOutline,
  mdiCheck,
  mdiClose,
} from '@mdi/js';

export default {
  name: 'CustomersTab',
  components: {
    CustomerForm,
    AdditionalInfo,
    UserMergeDialog,
    UserTransactionsDialog,
    ExportLinks,
    bSearchField,
    CustomerFilters,
    CustomerCouponsRedeem,
    CustomerRedemptionsDialog,
  },
  mixins: [translations],
  data() {
    return {
      icons: {
        mdiPencilOutline,
        mdiMerge,
        mdiAccountCash,
        mdiCellphoneCog,
        mdiGiftOff,
        mdiCardBulletedOff,
        mdiDotsHorizontal,
        mdiDeleteAlert,
        mdiDelete,
        mdiPlusMinusVariant,
        mdiGiftOpen,
        mdiReceiptTextCheckOutline,
        mdiCheck,
        mdiClose,
      },
      dialog: false,
      infoDialog: false,
      mergeDialog: false,
      transDialog: false,
      addCardDialog: false,
      changeMobileDialog: false,
      clearPointsDialog: false,
      detachCardDialog: false,
      importDialog: false,
      deleteCustomerDialog: false,
      deleteAttrDialog: false,
      deleteAttrTarget: null,
      deleteAttrValues: [],
      deleteAttrValueIds: [],
      columnsDialog: false,
      newCardNumber: '',
      newCardCvc: '',
      newMobile: '',
      newMobileCountryId: null,
      mode: '',
      search: '',
      customerDataDialog: false,
      clearPointsCustomer: null,
      detachCardCustomer: null,
      customersFile: null,
      deletingCustomerData: {},
      options: {},
      page: +this.$route.query.page,
      mergeUserId: null,
      transUserId: null,
      addCardCustomer: null,
      changeMobileUserAccount: null,
      debouncedReloadCustomers: () => {},
      adjustPointsDialog: false,
      adjustPoints: {
        user_account_id: null,
        amount: 0,
        loyalty_program_id: null,
        reason: '',
      },
      adjustPointsValidation: {
        amount: {
          errorMessage: '',
          success: false,
        },
        reason: {
          errorMessage: '',
          success: false,
        },
      },
      searchLoyalties: '',
      selectedLoyaltiesData: [],
      loyaltyPage: 1,
      redeemCouponsDialog: false,
      redeemCouponsCustomer: null,
      redemptionsDialog: false,
      redemptionsUserAccountId: null,
      attributeEdits: {},
    };
  },
  computed: {
    ...mapState([
      'loading',
      'superUser',
      'errorMessage',
      'serverItemsLength',
      'permissions',
      'storePermissions',
      'user',
    ]),
    ...mapState('loyaltyPanel', ['company']),
    ...mapState('loyaltyPanel/customers', ['customers', 'userPoints']),
    ...mapState('loyaltyPanel/catalogManagement', ['gifts']),
    ...mapGetters('loyaltyPanel/customers', ['storeCustomers', 'customerResponse']),
    ...mapGetters(['getPermission', 'activeCountries']),
    lang() {
      return this.$route.params.lang;
    },
    storeMode() {
      return this.$store.state.store?.display_mode || 'points';
    },

    attributeDefs() {
      return this.$store.state.loyaltyPanel.customers.attributes || [];
    },

    headers() {
      let headerData = [
        {
          text: this.translations.user_account_id[this.lang],
          value: 'user_account_id',
        },
        {
          text: this.translations.card_number[this.lang],
          value: 'card_number',
          maxWidth: 140,
        },
        {
          text: this.translations.mobile[this.lang],
          value: 'mobile',
        },
        {
          text: this.translations.email[this.lang],
          value: 'email',
        },
        {
          text: this.translations.firstName[this.lang],
          value: 'first_name',
        },
        {
          text: this.translations.lastName[this.lang],
          value: 'last_name',
        },
        {
          text: this.translations.total_transactions[this.lang],
          value: 'total_transactions',
        },
        {
          text: this.translations[`redeemed_${this.storeMode}`][this.lang],
          value: 'redeemed_points',
        },
        {
          text: this.translations[`current_balance_${this.storeMode}`][this.lang],
          value: 'current_balance',
        },
        {
          text: this.translations.total_price[this.lang],
          value: 'total_price',
        },
        {
          text: this.translations.storePointTitle[this.lang],
          value: 'store_point_title_name',
        },
        {
          text: this.translations.tierExpirationDate[this.lang],
          value: 'points_expires_at',
        },
        {
          text: this.translations.actions[this.lang],
          value: 'actions',
          sortable: false,
          width: 80,
        },
      ];
      if (this.superUser) {
        headerData.splice(-1, 0, {
          text: this.translations.user_id[this.lang],
          value: 'user_id',
          sortable: false,
          isVisible: true,
        });
      }

      const attrHeaders = (this.attributeDefs || []).map((a) => {
        const id = a.user_account_attribute_definition_id;
        const text = a.name;
        return {
          text,
          value: `custom_attr_${id}`,
          sortable: false,
          isVisible: true,
          _attrDef: a,
        };
      });
      const actionsIndex = headerData.findIndex((h) => h.value === 'actions');
      if (actionsIndex >= 0) {
        headerData.splice(actionsIndex, 0, ...attrHeaders);
      } else {
        headerData.push(...attrHeaders);
      }
      return headerData;
    },

    visibleHeaders() {
      let visibleHeaders = this.headers.map((e) => {
        const vis = this.visibleColumns.find((x) => x.column === e.value) || {};
        return {
          ...e,
          ...vis,
          text: vis.label || e.text,
        };
      });

      return visibleHeaders.filter((e) => e && e.isVisible);
    },

    visibleColumns: {
      get() {
        return this.$store.state.loyaltyPanel.customers.visibleColumns;
      },
      set(val) {
        this.setVisibleColumns(val);
      },
    },

    pageCustomers() {
      return (this.customers || []).map((o) => {
        const e = { ...o };

        for (let [k, v] of Object.entries(e)) {
          e[k] = v || '-';
        }

        (this.attributeDefs || []).forEach((a) => {
          const key = `custom_attr_${a.user_account_attribute_definition_id}`;
          e[key] = this.getAttributeValue(o, a);
        });

        return {
          ...e,
          mobile: e.mobile && !isNaN(e.mobile) ? e.mobile : '-',
        };
      });
    },

    query() {
      let query = '?';

      for (let key in this.$route.query) {
        query += `${key}=${this.$route.query[key]}&`;
      }
      if (this.search) {
        query += `q=${this.search}&`;
      }
      return query.slice(0, query.length - 1);
    },

    panelType: {
      get() {
        return this.$store.state.panelType;
      },
    },

    filtersData() {
      return this.$store.state.loyaltyPanel.customers.filtersData;
    },

    sendingData() {
      const filtered = {};
      let query = `?page=${this.page}`;
      for (const [key, value] of Object.entries(this.filtersData)) {
        if (value !== null && value !== undefined) {
          filtered[key] = value;
        }
      }
      if (this.search && this.search.trim() !== '') {
        query += `&q=${this.search}`;
      }
      return {
        query,
        filters: filtered,
      };
    },

    exportFilters() {
      const filtered = {};
      for (const [key, value] of Object.entries(this.filtersData)) {
        if (value !== null && value !== undefined && value !== '') {
          filtered[key] = value;
        }
      }
      return filtered;
    },

    loyalties() {
      return this.$store.state.loyaltyPanel.loyalties;
    },

    loyaltiesPagination() {
      return this.$store.state.loyaltyPanel.loyaltiesPagination;
    },
    loyaltiesData() {
      return [...this.loyalties, ...this.selectedLoyaltiesData];
    },
    loyaltyQuery() {
      if (this.searchLoyalties) {
        return `?q=${this.searchLoyalties}&page=${this.loyaltyPage}`;
      }

      return `?page=${this.loyaltyPage}`;
    },
  },
  methods: {
    ...mapMutations([
      'setResetValidation',
      'setResetSuccess',
      'setDialog',
      'setDeleteDialog',
      'setResetSuccess',
      'setResetValidation',
      'setNotification',
    ]),
    ...mapActions(['loadActiveCountries']),
    ...mapActions('loyaltyPanel', ['getLoyalties']),
    ...mapActions('loyaltyPanel/catalogManagement', ['getGifts']),
    ...mapMutations('loyaltyPanel/customers', [
      'setCustomerData',
      'setMergeDialogMobile',
      'setTransDialogUserAccId',
      'setTransDialogUserId',
      'setVisibleColumns',
      'updateCustomerData',
    ]),
    ...mapActions('loyaltyPanel/customers', [
      'loadCustomers',
      'getCustomerProfile',
      'loadStores',
      'addNewCardNumber',
      'updateMobile',
      'removePoints',
      'detachCard',
      'importCustomers',
      'loadCardStatus',
      'deleteCustomer',
      'mergeUsers',
      'getAttributes',
      'doAdjustPoints',
      'fetchUserPoints',
      'updateCustomerAttributes',
      'deleteCustomerAttribute',
    ]),

    getAttributeValue(item, attr) {
      const id = attr.user_account_attribute_definition_id;
      const sources = ['extra_attributes', 'custom_attributes'];

      for (const src of sources) {
        const arr = item && item[src];
        if (!Array.isArray(arr)) continue;

        if (attr.attribute_type === 'multi_select') {
          const foundAll = arr
            .filter((x) => x.user_account_attribute_definition_id == id)
            .map((f) => (f.value !== undefined && f.value !== null ? f.value : null))
            .filter((v) => v !== null);
          if (foundAll.length === 1) return foundAll[0];
          if (foundAll.length > 1) return foundAll;
        } else {
          const found = arr.find((x) => x.user_account_attribute_definition_id == id);
          if (found) return found.value !== undefined && found.value !== null ? found.value : '-';
        }
      }

      return '-';
    },

    formatDate(date) {
      if (!date || !moment(date).isValid()) return '-';
      return moment(date).format('DD/MM/YYYY');
    },

    handleSearch() {
      this.loadCustomers(this.sendingData);
    },
    getCustomerActionItems(item) {
      const lang = this.lang;
      const actions = [
        {
          key: 'update',
          icon: this.icons.mdiPencilOutline,
          label: this.translations.update[lang],
          disabled: !this.getPermission('customers.update'),
          onClick: () => this.openCustomerDataDialog('update', item),
        },
      ];

      if (this.getPermission('customers.change_mobile')) {
        actions.push({
          key: 'changeMobile',
          icon: this.icons.mdiCellphoneCog,
          label: this.translations.changeMobile[lang],
          disabled: false,
          onClick: () => this.openChangeMobileDialog(item),
        });
      }
      if (this.superUser) {
        actions.push({
          key: 'merge',
          icon: this.icons.mdiMerge,
          label: this.translations.merge[lang],
          disabled: false,
          onClick: () => this.openUserMergeDialog(item),
        });
      }
      if (this.getPermission('customers?.read')) {
        actions.push({
          key: 'transactions',
          icon: this.icons.mdiAccountCash,
          label: this.translations.transactions[lang],
          disabled: false,
          onClick: () => this.openUserTransactionsDialog(item),
        });
      }
      if (this.superUser && item.current_balance) {
        actions.push({
          key: 'clear',
          icon: this.icons.mdiGiftOff,
          label: this.translations[`clear_${this.storeMode}`][lang],
          disabled: false,
          onClick: () => this.openClearPointsDialog(item),
        });
      }
      if (this.superUser && item.cards != '-') {
        actions.push({
          key: 'detachCard',
          icon: this.icons.mdiCardBulletedOff,
          label: this.translations.detachCard[lang],
          disabled: false,
          onClick: () => this.openDetachCardDialog(item),
        });
      }
      if (this.getPermission('customers?.delete')) {
        actions.push({
          key: 'delete',
          icon: this.icons.mdiDeleteAlert,
          label: this.translations.deletePermanently[lang],
          disabled: false,
          onClick: () => this.openDeleteCustomerDialog(item),
        });
      }
      if (this.getPermission('customers?.update')) {
        actions.push({
          key: 'adjust',
          icon: this.icons.mdiPlusMinusVariant,
          label: this.translations[`adjust_${this.storeMode}Tooltip`][lang],
          disabled: false,
          onClick: () => this.openAdjustPointsDialog(item),
        });
      }
      actions.push({
        key: 'redeemCoupons',
        icon: this.icons.mdiGiftOpen,
        label: this.translations.redeemCouponsTooltip[lang],
        disabled: false,
        onClick: () => this.openRedeemCouponsDialog(item),
      });
      if (this.superUser) {
        actions.push({
          key: 'redemptions',
          icon: this.icons.mdiReceiptTextCheckOutline,
          label: this.translations.redemptions[lang],
          disabled: false,
          onClick: () => this.openCustomerRedemptionsDialog(item),
        });
      }

      return actions;
    },
    runCustomerAction(action) {
      if (action.disabled || !action.onClick) return;
      action.onClick();
    },
    openChangeMobileDialog(item) {
      this.changeMobileUserAccount = { ...item };
      this.newMobileCountryId = this.changeMobileUserAccount.country_id || null;
      this.changeMobileDialog = true;
    },

    closeChangeMobileDialog() {
      this.changeMobileUserAccount = null;
      this.newMobile = '';
      this.newMobileCountryId = null;
      this.changeMobileDialog = false;
    },

    async saveNewMobile() {
      if (this.changeMobileUserAccount?.user_account_id) {
        let res = await this.updateMobile({
          user_account_id: this.changeMobileUserAccount.user_account_id,
          mobile: this.newMobile,
          country_id: this.newMobileCountryId,
        });
        if (res && !res.error) {
          this.setNotification({
            show: true,
            type: 'success',
            text: res?.success || 'Success',
          });
          this.loadCustomers(this.sendingData);
        }
      }
      this.closeChangeMobileDialog();
    },

    openClearPointsDialog(item) {
      this.clearPointsCustomer = { ...item };
      this.clearPointsDialog = true;
    },

    closeClearPointsDialog() {
      this.clearPointsCustomer = null;
      this.clearPointsDialog = false;
    },

    async removeAllPoints() {
      if (this.clearPointsCustomer?.user_account_id) {
        let res = await this.removePoints(this.clearPointsCustomer.user_account_id);
        if (res && !res.error) {
          this.setNotification({
            show: true,
            type: 'success',
            text: res?.success || 'Success',
          });
          this.loadCustomers(this.sendingData);
        }
      }
      this.closeClearPointsDialog();
    },

    openDetachCardDialog(item) {
      this.detachCardCustomer = { ...item };
      this.detachCardCustomer.card_number = null;
      if (this.detachCardCustomer.cards.length === 1) {
        this.detachCardCustomer.card_number = this.detachCardCustomer.cards[0];
      }
      this.detachCardDialog = true;
    },

    closeDetachCardDialog() {
      this.detachCardCustomer = null;
      this.detachCardDialog = false;
    },

    async detachCustomerCard() {
      if (this.detachCardCustomer?.user_account_id) {
        let res = await this.detachCard(this.detachCardCustomer);
        if (res && !res.error) {
          this.setNotification({
            show: true,
            type: 'success',
            text: res?.success || 'Success',
          });
          this.loadCustomers(this.sendingData);
        }
      }
      this.closeDetachCardDialog();
    },

    openUserMergeDialog(item) {
      this.setMergeDialogMobile(item.mobile);
      this.mergeUserId = item.mobile;
      this.mergeDialog = true;
    },

    openUserTransactionsDialog(item) {
      this.setTransDialogUserAccId(item.user_account_id);
      this.setTransDialogUserId(item.user_id);
      this.transUserId = item.user_account_id;
      this.transDialog = true;
    },

    openAddCardDialog(customer) {
      this.addCardDialog = true;
      this.addCardCustomer = customer;
    },

    async saveCardNumber() {
      const body = {
        company_id: this.company.store_id,
        user_account_id: this.addCardCustomer?.user_account_id,
        card_number: this.newCardNumber,
      };
      await this.addNewCardNumber(body);
      this.addCardDialog = false;
    },

    openDeleteCustomerDialog(item) {
      this.deletingCustomerData = cloneDeep(item);
      this.deleteCustomerDialog = true;
    },

    openImportDialog() {
      this.importDialog = true;
    },

    openCustomerDataDialog(mode, item) {
      this.mode = mode;
      if (mode == 'create') {
        this.setCustomerData({});
        this.dialog = true;
      } else if (mode == 'update') {
        this.getCustomerProfile({
          id: item.user_account_id,
          mode: 'update',
        });
      }
      this.customerDataDialog = true;

      setTimeout(() => this.setResetSuccess(true), 300);
      this.setResetValidation(true);
    },

    closeCustomerDataDialog() {
      this.customerDataDialog = false;
    },

    async uploadCustomersFile() {
      let res = await this.importCustomers(this.customersFile);
      if (res && !res.error) {
        this.setNotification({
          show: true,
          type: 'success',
          text: res?.success || 'Success',
        });
        this.customersFile = null;
        await this.loadCustomers(this.sendingData);
      }
      this.importDialog = false;
    },

    async deletingCustomer() {
      let res = this.deleteCustomer(this.deletingCustomerData.user_account_id);
      if (res && !res.error) {
        this.setNotification({
          show: true,
          type: 'success',
          text: this.translations.deleteCustomerSuccess[this.lang],
        });
      } else {
        this.setNotification({
          show: true,
          type: 'error',
          text: res?.message || 'Error',
        });
      }

      this.deletingCustomerData = {};
      this.deleteCustomerDialog = false;
    },

    async startMergeUsers(body) {
      let res = await this.mergeUsers(body);
      if (res?.data && !res.data.error) {
        this.loadCustomers(this.sendingData);
        this.setNotification({
          show: true,
          type: 'success',
          text: this.translations.mergeSuccess[this.lang],
        });
        this.mergeDialog = false;
      } else {
        this.setNotification({
          show: true,
          type: 'error',
          text: res?.data?.message || 'Error',
        });
      }
    },
    adjustStorageColumns() {
      const rebuilt = this.headers.map((header) => {
        const existing = (this.visibleColumns || []).find((x) => x.column == header.value);
        if (existing) {
          if (!existing.label || existing.label === existing.column) {
            existing.label = header.text;
          }
          return {
            ...existing,
            column: header.value,
            label: existing.label || header.text,
          };
        }

        return {
          column: header.value,
          isVisible: true,
          label: header.text,
        };
      });

      this.visibleColumns = rebuilt;
    },
    getVisibleColumnLabel(column) {
      const header = this.headers.find((h) => h.value === column.column);
      if (header && header.text) return header.text;
      if (column && column.label) return column.label;
      if (this.translations[column.column]) return this.translations[column.column][this.lang];
      return column.column;
    },
    reloadCustomersWithFilters() {
      this.page = 1;
      this.loadCustomers(this.sendingData);
    },

    openAdjustPointsDialog(item) {
      this.adjustPoints.user_account_id = item.user_account_id;
      this.adjustPoints.amount = 0;
      this.adjustPoints.loyalty_program_id = this.company?.loyalty_program_id || null;
      this.adjustPoints.reason = '';
      this.adjustPointsDialog = true;
    },
    closeAdjustPointsDialog() {
      this.adjustPoints.user_account_id = null;
      this.adjustPoints.amount = 0;
      this.adjustPoints.loyalty_program_id = null;
      this.adjustPoints.reason = '';
      this.adjustPointsValidation = {
        amount: {
          errorMessage: '',
          success: false,
        },
        reason: {
          errorMessage: '',
          success: false,
        },
      };
      this.adjustPointsDialog = false;
    },
    validateAdjustPoints() {
      let success = false;
      this.validatePointsAmount();
      this.validatePointsReason();
      success =
        this.adjustPointsValidation.amount.success && this.adjustPointsValidation.reason.success;
      return success;
    },

    async submitAdjustPoints() {
      let errorMsg =
        this.adjustPointsValidation.amount.error || this.adjustPointsValidation.reason.error;
      if (!this.adjustPoints.loyalty_program_id || !this.validateAdjustPoints()) {
        this.setNotification({
          show: true,
          type: 'error',
          text: errorMsg || 'Error',
        });
        return;
      }

      const body = {
        user_account_id: this.adjustPoints.user_account_id,
        loyalty_program_id: this.adjustPoints.loyalty_program_id,
        points: String(this.adjustPoints.amount),
        reason: this.adjustPoints.reason,
      };
      let res = await this.doAdjustPoints(body);
      if (res && !res.error) {
        this.setNotification({
          show: true,
          type: 'success',
          text: this.translations[`adjust_${this.storeMode}Success`][this.lang],
        });
      }
      this.closeAdjustPointsDialog();
    },

    removeSelectedLoyalty() {
      this.adjustPoints.loyalty_program_id = null;
    },
    endIntersectLoyalties(entries, observer, isIntersecting) {
      if (isIntersecting && this.loyalties?.length < this.loyaltiesPagination.total) {
        this.loadMoreLoyalties();
      }
    },

    async loadMoreLoyalties() {
      this.loyaltyPage++;
      const body = {
        query: this.loyaltyQuery,
        page: this.loyaltyPage,
      };
      this.setLoading(true);
      await this.getLoyalties(body);
      this.setLoading(false);
    },
    handleSearchLoyalties() {
      const body = {
        query: this.loyaltyQuery,
        page: this.loyaltyPage,
      };

      this.getLoyalties(body);
    },

    validatePointsAmount() {
      const value = this.adjustPoints.amount;
      this.adjustPointsValidation.amount.errorMessage = '';
      this.adjustPointsValidation.amount.success = false;
      if (!Number.isInteger(value) || value === 0 || !value) {
        this.adjustPointsValidation.amount.errorMessage =
          this.translations[`invalid${this.storeMode}Amount`][this.lang];
        this.adjustPointsValidation.amount.success = false;
      } else {
        this.adjustPointsValidation.amount.errorMessage = '';
        this.adjustPointsValidation.amount.success = true;
      }
    },

    validatePointsReason() {
      const value = this.adjustPoints.reason;
      this.adjustPointsValidation.reason.errorMessage = '';
      this.adjustPointsValidation.reason.success = false;
      if (!value) {
        this.adjustPointsValidation.reason.errorMessage =
          this.translations[`invalid${this.storeMode}Reason`][this.lang];
        this.adjustPointsValidation.reason.success = false;
      } else {
        this.adjustPointsValidation.reason.errorMessage = '';
        this.adjustPointsValidation.reason.success = true;
      }
    },

    openRedeemCouponsDialog(item) {
      this.redeemCouponsCustomer = { ...item };
      this.redeemCouponsDialog = true;
      this.fetchUserPoints(item.user_account_id);
    },
    openCustomerRedemptionsDialog(item) {
      this.redemptionsUserAccountId = item.user_account_id;
      this.redemptionsDialog = true;
    },
    closeCustomerRedemptionsDialog() {
      this.redemptionsDialog = false;
      this.redemptionsUserAccountId = null;
    },
    closeRedeemCouponsDialog() {
      this.redeemCouponsCustomer = null;
      this.redeemCouponsDialog = false;
    },

    isEditing(user_account_id, attrKey) {
      return this.attributeEdits[user_account_id]?.[attrKey]?.editing;
    },

    startEditAttribute(item, attrDef) {
      const id = attrDef.user_account_attribute_definition_id;
      const colKey = `custom_attr_${id}`;
      const userId = item.user_account_id;

      if (this.attributeEdits[userId]) {
        Object.keys(this.attributeEdits[userId]).forEach((key) => {
          const ed = this.attributeEdits[userId][key];
          if (ed && ed.editing) {
            const col = item[key];
            if (col !== undefined) item[key] = ed.original;
            this.$delete(this.attributeEdits[userId], key);
          }
        });
      }

      if (!this.attributeEdits[userId])
        this.$set(this.attributeEdits, userId, {});
      let initialValue = item[colKey] === '-' ? '' : item[colKey];
      let value_defs = attrDef.user_account_attribute_definition_value_ids.map((v) => {
        let key = Object.keys(v)[0];
        return { id: Number(key), value: v[key] };
      });
      if (attrDef.attribute_type === 'multi_select') {
        if (Array.isArray(item[colKey])) initialValue = item[colKey];
        else if (typeof item[colKey] === 'string' && item[colKey].includes(','))
          initialValue = item[colKey].split(',').map((s) => s.trim());
        else if (item[colKey] === '-' || item[colKey] === null || item[colKey] === undefined)
          initialValue = [];
        else initialValue = [item[colKey]];
      }

      this.$set(this.attributeEdits[item.user_account_id], colKey, {
        editing: true,
        value: initialValue,
        attr_id: id,
        value_defs,
        original: item[colKey],
        loading: false,
        editDateMenu: false,
        editTimeMenu: false,
        editDateVal: null,
        editTimeVal: null,
      });
    },

    cancelEditAttribute(item, attrDef) {
      const id = attrDef.user_account_attribute_definition_id;
      const colKey = `custom_attr_${id}`;
      const ed =
        this.attributeEdits[item.user_account_id] &&
        this.attributeEdits[item.user_account_id][colKey];
      if (ed) {
        item[colKey] = ed.original;
        this.$delete(this.attributeEdits[item.user_account_id], colKey);
      }
      this.updateCustomerData(item);
    },

    async saveAttribute(item, attrDef) {
      const attrId = attrDef.user_account_attribute_definition_id;
      const colKey = `custom_attr_${attrId}`;
      const ed = this.attributeEdits[item.user_account_id]?.[colKey];
      if (!ed) return;
      if (ed.value === ed.original) {
        this.$delete(this.attributeEdits[item.user_account_id], colKey);
        return;
      }
      let valDef;
      let valId;
      if (Array.isArray(ed.value)) {
        valId = [];
        ed.value.forEach((val) => {
          valDef = ed.value_defs.find((v) => String(v.value) === String(val));
          if (valDef) valId.push(valDef.id);
        });
      } else {
        valDef = ed.value_defs.find((v) => String(v.value) === String(ed.value));
        valId = valDef ? valDef.id : null;
      }
      if (attrDef.attribute_type === 'text' && attrDef.value_type === 'number') {
        if (
          ed.value !== '' &&
          ed.value !== null &&
          ed.value !== undefined &&
          !isFinite(Number(ed.value))
        ) {
          this.setNotification({
            show: true,
            type: 'error',
            text: this.translations.invalidNumberValue?.[this.lang] || 'error',
          });
          return;
        }
      }
      this.$delete(this.attributeEdits[item.user_account_id], colKey);

      ed.loading = true;
      let payloadValue = ed.value;
      if (
        payloadValue === '' ||
        payloadValue === null ||
        (Array.isArray(payloadValue) && payloadValue.length === 0)
      ) {
        this.setNotification({
          show: true,
          type: 'error',
          text: this.translations.attributeSaveNoValue?.[this.lang],
        });
        ed.loading = false;
        return;
      }

      const previous = item[colKey];
      item[colKey] = payloadValue;

      try {
        let res;
        const payload = {
          user_account_id: item.user_account_id,
          attribute_id: attrId,
          value: payloadValue,
        };
        if (attrDef.attribute_type === 'text') {
          payload.value_id = null;
        } else {
          payload.value_id = valId || null;
        }
        res = await this.updateCustomerAttributes(payload);

        this.setNotification({
          show: true,
          type: 'success',
          text: (res?.message && res.message) || this.translations.saved[this.lang] || 'Success',
        });
        if (!res.error && res.data) {
          this.updateCustomerData(res.data);
        }
      } catch (err) {
        item[colKey] = previous;
        this.setNotification({
          show: true,
          type: 'error',
          text: err.response?.data?.message || 'Error',
        });
      } finally {
        ed.loading = false;
      }
    },

    setDateTimeInlineEdit(item, attr) {
      const colKey = `custom_attr_${attr.user_account_attribute_definition_id}`;
      const ed = this.attributeEdits[item.user_account_id]?.[colKey];
      if (!ed) return;
      ed.value = (ed.editDateVal || '') + ' ' + (ed.editTimeVal || '');
    },

    confirmDeleteAttribute(item, attr) {
      const attrId = attr.user_account_attribute_definition_id;
      const colKey = `custom_attr_${attrId}`;
      const raw = item[colKey];
      let valuesArr = [];
      if (raw === null || raw === undefined || raw === '' || raw === '-') valuesArr = [];
      else if (Array.isArray(raw)) valuesArr = raw;
      else if (typeof raw === 'string' && raw.includes(','))
        valuesArr = raw.split(',').map((s) => s.trim());
      else valuesArr = [raw];

      let valueIds = [];
      const arr = item?.['extra_attributes'];
      if (Array.isArray(arr)) {
        const matchingAttrs = arr.filter((x) => x.user_account_attribute_definition_id == attrId);
        matchingAttrs.forEach((a) => {
          const vid = a.user_account_attribute_definition_value_id;
          if (vid) {
            valueIds.push(vid);
          }
        });
      }

      this.deleteAttrTarget = { item, attr, attrId, colKey };
      this.deleteAttrValues = valuesArr;
      this.deleteAttrValueIds = valueIds;
      this.deleteAttrDialog = true;
    },

    async performDeleteAttribute() {
      const t = this.deleteAttrTarget;
      if (!t) {
        this.deleteAttrDialog = false;
        return;
      }

      const { item, attr, attrId } = t;
      if (!(this.deleteAttrValueIds && this.deleteAttrValueIds.length > 0)) {
        this.updateCustomerData(item);
      }
      try {
        let res;

        if (this.deleteAttrValueIds && this.deleteAttrValueIds.length > 0) {
          for (const vid of this.deleteAttrValueIds) {
            res = await this.deleteCustomerAttribute({
              user_account_id: item.user_account_id,
              attribute_id: attrId,
              value_id: vid,
            });
          }
        }

        this.setNotification({
          show: true,
          type: 'success',
          text: (res?.message && res.message) || this.translations.saved[this.lang] || 'Success',
        });
        this.loadCustomers(this.sendingData);
      } catch (err) {
        this.setNotification({
          show: true,
          type: 'error',
          text: err.response?.data?.message || 'Error',
        });
      } finally {
        this.deleteAttrDialog = false;
        this.deleteAttrTarget = null;
        this.deleteAttrValues = [];
        this.deleteAttrValueIds = [];
      }
    },

    displayMultiValues(item, attr) {
      const id = attr.user_account_attribute_definition_id;
      const colKey = `custom_attr_${id}`;
      let val = item[colKey];
      if (val === null || val === undefined || val === '' || val === '-') return [];
      let arr = [];
      if (Array.isArray(val)) arr = val;
      else if (typeof val === 'string' && val.includes(','))
        arr = val.split(',').map((s) => s.trim());
      else arr = [val];

      const options = attr.values || [];

      return arr.map((v) => {
        const found = options.find((opt) => {
          if (!opt) return false;
          if (!opt.user_account_attribute_definition_value_id && !opt.value && !opt.id) {
            const key = Object.keys(opt)[0];
            return String(key) === String(v);
          }
          return opt.user_account_attribute_definition_value_id == v;
        });
        if (found) return found.label || v;
        return v;
      });
    },
  },

  watch: {
    customerDataDialog(val) {
      if (!val) {
        this.closeCustomerDataDialog();
      }
    },

    options: {
      handler(val, oldVal) {
        if (val.sortBy[0]) {
          if (oldVal.sortDesc[0] === undefined || oldVal.sortDesc[0] !== val.sortDesc[0]) {
            if (this.page !== 1) {
              this.page = 1;
            } else {
              this.loadCustomers(this.sendingData);
            }
          }
        }
      },
      deep: true,
    },
    visibleColumns: {
      handler(val, oldVal) {
        if (val) {
          this.setVisibleColumns(val);
        }
      },
      deep: true,
    },
    $route(val) {
      if (!val.query.page) {
        this.$router.push({
          query: {
            page: 1,
            ...this.$route.query,
          },
        });
      }
      this.loadCustomers(this.sendingData);
    },

    page(page) {
      this.$router.push({ query: { ...this.$route.query, page } });
    },

    search(val, oldVal) {
      if (val != oldVal) {
        this.page = 1;
      }
      if (val == null) {
        this.loadCustomers(this.sendingData);
      } else {
        this.debouncedSearch();
      }
    },

    permissions: {
      handler(val, oldVal) {
        if (val && !this.getPermission('customers?.read')) {
          this.$router.replace(`/${this.lang}/${this.panelType}/forbidden-gateway`);
        }
      },
      deep: true,
    },
    filtersData: {
      handler() {
        this.debouncedReloadCustomers();
      },
      deep: true,
    },
    attributeDefs: {
      handler(val) {
        if (val && val.length) {
          this.adjustStorageColumns();
        }
      },
      deep: true,
    },
    searchLoyalties() {
      this.loyaltyPage = 1;
      this.debouncedSearchLoyalties();
    },
    adjustPointsDialog(val) {
      if (!val) {
        this.closeAdjustPointsDialog();
      }
    },
  },

  beforeCreate() {
    if (!this.$route.query.page) {
      this.$router.push({
        query: {
          page: 1,
          ...this.$route.query,
        },
      });
    }
  },

  created() {
    this.debouncedSearch = debounce(this.handleSearch, 500);
    this.debouncedReloadCustomers = debounce(this.reloadCustomersWithFilters, 800);
    this.debouncedSearchLoyalties = debounce(this.handleSearchLoyalties, 500);
  },

  mounted() {
    this.loadCustomers(this.sendingData);
    this.loadActiveCountries();
    this.adjustStorageColumns();
    this.getAttributes();
    this.handleSearchLoyalties();
  },
};
</script>

<style>
.country-code-select .v-input__slot {
  min-width: 110px;
  padding: 0 4px 0 8px !important;
}
.country-code-select .v-select__selections input {
  padding: 0 !important;
  margin: 0 !important;
  height: 0 !important;
  width: 0 !important;
  overflow: hidden !important;
}
.country-code-select .v-input__append-inner {
  padding-left: 0 !important;
}
.country-code-select .v-input__append-inner .v-icon.v-icon::after {
  max-width: 20px;
  min-width: 20px;
}
.country-code-select fieldset {
  border-right: 1px dotted black !important;
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
}
.user-mobile fieldset {
  border-left: none !important;
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
}
.mobile-cc-container .v-text-field__details {
  height: 25px;
}
</style>
